import { IconButton } from "@carbon6/c6-frontend-design";
import { useCallback, type ReactNode } from "react";
import { useNavigate, useSearchParams, type NavigateFunction } from "react-router";
import { GnbMenu } from "@/components/base";
import {
  DatasetCard,
  DatasetPageContent,
  DatasetViewModeToggle,
  createThumbnailRefreshDebouncer,
  type DatasetViewMode,
} from "@/components/dataset";
import { PageBase, type PageBaseContext, type PageBaseProps } from "@/page/base/PageBase";
import {
  DatasetFilterBlock,
  DatasetFilterController,
  type DatasetFilterDefaultValues,
} from "@/components/filter";
import { windowStack } from "@/app/windowStack";
import {
  applyUrlPageToSource,
  goToNextPage,
  goToPreviousPage,
  mirrorSourcePageToUrl,
  pageSearchParamKey,
  parsePageParam,
  writePageParam,
} from "@/utils";
import { resolveErrorMessage } from "@/components/dataset";
import type { DatasetTableRow } from "@/page/dataset/DatasetTableRow";
import { DatasetTableSource } from "@/page/dataset/DatasetTableSource";
import { buildDatasetReviewQueueColumns } from "./datasetReviewQueueColumns";
import { createReviewDatasetCardModel } from "./reviewDatasetCardModel";
import { activeReviewDataset, openDatasetDetailWindow } from "@/app/datasetDetailWindow";

const defaultPageSize = 20;
const reviewOpenParam = "review";
// 서비스 앱이 소유하는 Reviews 필터 초기화 기준. 큐의 reviewed=false 고정 축과는 별개다.
const reviewFilterDefaults: DatasetFilterDefaultValues = {
  searchQuery: "",
  selection: {},
  labelTokens: [],
  matchMode: "all",
};

// 리뷰 페이지. 확정 대상 데이터셋 큐(grid/table + 섬네일) 하나만 둔다. 행이나 카드에서 데이터셋
// 리뷰 창(review 모드)을 연다. 검토는 통합 configuration review 하나다. 창은 데이터셋마다 하나이므로
// 여러 창을 동시에 열 수 있다. ?review= 는 그중 '지금 활성인 창'을 반영한다.
export function ReviewPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewUuid = searchParams.get(reviewOpenParam);
  const page = parsePageParam(searchParams.get(pageSearchParamKey));

  const setPage = useCallback(
    (next: number) => {
      setSearchParams((prev) => writePageParam(prev, next, pageSearchParamKey), { replace: true });
    },
    [setSearchParams],
  );

  // ?review 동기화. 활성 리뷰 창의 uuid 를 그대로 반영한다(없으면 제거). 다중 창에서 이 값은 창을
  // 닫는 동작이 아니라 '활성 창 표시'다. 그래서 히스토리에 항목을 쌓지 않도록 항상 replace 로 쓴다.
  // 뒤로가기는 창을 토글하지 않고 이전 경로로 나간다.
  // 이 함수는 ReviewPageView 의 stack↔URL reconcile 만 호출한다.
  const setReviewParam = useCallback(
    (uuid: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (uuid === null) {
            next.delete(reviewOpenParam);
          } else {
            next.set(reviewOpenParam, uuid);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <ReviewPageView
      pageTitle="Reviews"
      currentMenu={GnbMenu.Review}
      requiredRoles={["admin"]}
      scrollable
      // belowTabs 조건 블록이 이미 헤더와 본문을 구분한다. 그래서 본문 상단 여백을 더하지 않는다
      // (Datasets 와 동일).
      bodyFlushTop
      reviewUuid={reviewUuid}
      navigate={navigate}
      page={page}
      onPageChange={setPage}
      onReviewParamChange={setReviewParam}
    />
  );
}

type ReviewPageViewProps = PageBaseProps & {
  reviewUuid: string | null;
  navigate: NavigateFunction;
  page: number;
  onPageChange: (page: number) => void;
  onReviewParamChange: (uuid: string | null) => void;
};

type ReviewPageViewState = {
  viewMode: DatasetViewMode;
};

class ReviewPageView extends PageBase<ReviewPageViewProps, ReviewPageViewState> {
  override state: ReviewPageViewState = { viewMode: "grid" };
  private disposed = false;
  // 행 클릭이나 카드 버튼이 전역 리뷰 창을 연다. URL(?review) 동기화는 stack↔URL reconcile 가
  // 한다. 여기서는 URL 을 직접 쓰지 않는다. 생성자에서 rowClickHandler 로 참조하려면
  // datasetsSource 보다 먼저 선언해야 한다.
  private readonly openReview = (row: DatasetTableRow): void => {
    this.openSession(row.uuid, row.display_name);
  };

  // 리뷰는 공용 데이터셋 창의 `데이터셋 리뷰` 탭(candidate 모드)을 연다. 사용자는 제출된 변경
  // 요청을 편집해 확정한다. 같은 창의 에피소드 탭에서 에피소드 판정도 이어서 한다.
  // 창이 닫히면(확정·취소 무관) 큐를 새로고침해 남은 대상만 표시한다.
  private openSession(datasetUuid: string, displayName: string): void {
    openDatasetDetailWindow({
      datasetUuid,
      displayName,
      mode: "candidate",
      initialTab: "review",
      onClose: () => {
        void this.datasetsSource.refresh();
      },
    });
  }

  // 큐는 Dataset 목록의 `review_queue=true` 조회다. 아직 확정되지 않았거나 변경 요청이 제출된
  // Dataset 을 담는다. 행·컬럼·카드·소스를 Dataset 페이지와 공유하고 이 축 하나만 고정한다.
  // 목록 액션(북마크, 선택)은 리뷰 화면에 두지 않으므로 행 액션은 비운다.
  private readonly datasetsSource = new DatasetTableSource({
    pageSize: defaultPageSize,
    rowActionsBuilder: () => null,
    rowClickHandler: this.openReview,
    resolveErrorMessage,
    reviewQueueOnly: true,
  });

  // 검색과 facet 필터(Dataset·북마크와 공유하는 DatasetFilter). 같은 엔드포인트를 쓰므로 facet
  // 표면도 같다.
  private readonly datasetFilterController = new DatasetFilterController({
    defaultValues: reviewFilterDefaults,
    onSearchQueryChange: (query) => {
      void this.datasetsSource.setSearchQuery(query);
    },
    onFilterSelectionChange: (selection, mode) => {
      void this.datasetsSource.setFilterSelection(selection, mode);
    },
    resolveErrorMessage,
  });

  // 섬네일 서명 URL 이 만료되면 디바운스(60s)해서 다시 조회한다.
  private readonly handleThumbnailExpired = createThumbnailRefreshDebouncer(() => {
    void this.datasetsSource.refresh();
  });

  private windowStackUnsubscribe: (() => void) | null = null;

  private readonly handleSourceChange = (): void => {
    if (this.disposed) {
      return;
    }
    const activeSource = this.datasetsSource;
    mirrorSourcePageToUrl(activeSource, this.props.page, this.props.onPageChange);
    this.forceUpdate();
  };

  // 창 스택을 URL 에 반영한다. ?review 는 '지금 활성인 리뷰 창'의 datasetUuid 다. 활성 창이 direct
  // 모드 상세 창이거나 모든 창이 최소화되었으면 null 이다. 창은 app-level 이라 라우터에 접근할 수
  // 없다. 그래서 이 페이지만 이 값을 쓴다.
  private readonly handleWindowStackChange = (): void => {
    if (this.disposed) {
      return;
    }
    const target = activeReviewDataset();
    if (target === this.props.reviewUuid) {
      return;
    }
    this.props.onReviewParamChange(target);
  };

  protected override async initPage(): Promise<void> {
    this.disposed = false;
    this.datasetFilterController.init();
    this.datasetsSource.subscribe(this.handleSourceChange);
    this.datasetFilterController.subscribe(() => {
      if (!this.disposed) {
        this.forceUpdate();
      }
    });
    this.windowStackUnsubscribe = windowStack.subscribe(this.handleWindowStackChange);
    await Promise.all([this.loadQueue(), this.datasetFilterController.loadCatalog()]);
    this.syncModalFromUrl();
    // 마운트 시점에 이미 열린 활성 리뷰 창을 URL 에 한 번 반영한다. 그 뒤에는 구독이 반영한다.
    this.handleWindowStackChange();
  }

  protected override disposePage(): void {
    this.disposed = true;
    this.datasetFilterController.dispose();
    this.datasetsSource.dispose();
    this.windowStackUnsubscribe?.();
    this.windowStackUnsubscribe = null;
  }

  componentDidUpdate(prevProps: ReviewPageViewProps): void {
    if (prevProps.page !== this.props.page) {
      applyUrlPageToSource(this.datasetsSource, this.props.page);
    }
    // URL ?review 변화(딥링크, 뒤로가기, 탭 전환에 따른 제거 등)를 모달 상태에 반영한다.
    if (prevProps.reviewUuid !== this.props.reviewUuid) {
      this.syncModalFromUrl();
    }
  }

  private async loadQueue(): Promise<void> {
    await this.datasetsSource.goToPage(this.props.page);
  }

  // URL 을 창에 반영한다. ?review=uuid 가 있으면 그 데이터셋 리뷰 창을 연다. 이미 열려 있으면
  // 활성화하거나 복원한다. 다중 창에서는 URL 이 비어도 창을 닫지 않는다. 창은 [X] 로만 닫는다.
  // ?review 는 활성 리뷰 창의 표시일 뿐이다. 값이 비면 다른 창으로 옮겼다는 뜻이지 종료가 아니다.
  private syncModalFromUrl(): void {
    const uuid = this.props.reviewUuid;
    if (uuid === null || uuid === "") {
      return;
    }
    // 그 데이터셋 창이 이미 열려 있으면 open 이 기존 창을 복원한다(데이터셋당 1창). 행 데이터가
    // 없을 수 있으므로 이름은 uuid 로 둔다. 확정 뒤 큐 새로고침은 onClose 가 한다.
    this.openSession(uuid, uuid);
  }

  private readonly handleViewModeChange = (viewMode: DatasetViewMode): void => {
    this.setState({ viewMode });
  };

  private readonly handleRefreshClick = (): void => {
    void this.datasetFilterController.loadCatalog();
    void this.datasetsSource.refresh();
  };

  private readonly handlePreviousPageClick = (): void => {
    const activeSource = this.datasetsSource;
    goToPreviousPage(activeSource, this.props.onPageChange);
  };

  private readonly handleNextPageClick = (): void => {
    const activeSource = this.datasetsSource;
    goToNextPage(activeSource, this.props.onPageChange);
  };

  // 검색, 필터 레일, 고급 필터 진입은 Datasets 와 같은 조건 블록으로 헤더 belowTabs 슬롯에 둔다.
  // 두 화면은 같은 필터 표면을 쓴다(rules.md §17). 큐 전용 축(reviewed=false)은 소스가 고정하고,
  // 블록에는 없다.
  protected override renderBelowTabs(context: PageBaseContext): ReactNode {
    return (
      <DatasetFilterBlock
        controller={this.datasetFilterController}
        bookmarkSurface="review"
        bookmarkUid={context.currentUserUid}
        searchPlaceholder="리뷰할 Dataset 이름으로 검색하거나 라벨 조건을 골라 주세요."
        searchAriaLabel="리뷰할 Dataset 검색"
      />
    );
  }

  protected override renderContent(): ReactNode {
    return this.renderDatasetsTab();
  }

  // 표시 조립(BulkActionBar 띠, 카드/테이블 뷰, 페이지네이션)은 Datasets 와 같은 DatasetPageContent
  // 가 소유한다. 결과 수가 일괄 액션 자리 오른쪽에 있는 것도 같다. 리뷰 큐는 선택 액션이 없으므로
  // 결과 수가 띠의 첫 항목이다. 뷰 토글과 새로고침은 바 우측 슬롯에 둔다.
  private renderDatasetsTab(): ReactNode {
    const isLoading = this.datasetsSource.getSnapshot().loadState === "loading";
    const columns = buildDatasetReviewQueueColumns({
      onThumbnailError: this.handleThumbnailExpired,
    });

    return (
      <DatasetPageContent
        source={this.datasetsSource}
        columns={columns}
        renderCard={(row) => (
          <DatasetCard
            item={createReviewDatasetCardModel(row)}
            imageUrl={row.thumbnail_url}
            onImageError={this.handleThumbnailExpired}
          />
        )}
        viewMode={this.state.viewMode}
        summaryLabel="리뷰할 Dataset"
        layoutAriaLabel="Dataset 리뷰 큐"
        viewAriaLabel="Dataset 리뷰"
        emptyMessage="리뷰가 필요한 Dataset이 없습니다."
        loadingMessage="리뷰 대상을 불러오고 있습니다."
        minWidth="1080px"
        barRightContent={
          <>
            <IconButton
              size="sm"
              text={isLoading ? "목록을 불러오고 있습니다." : "새로고침"}
              variant="surface"
              disabled={isLoading}
              onClick={this.handleRefreshClick}
            />
            <DatasetViewModeToggle
              size="sm"
              value={this.state.viewMode}
              onChange={this.handleViewModeChange}
            />
          </>
        }
        onPreviousPage={this.handlePreviousPageClick}
        onNextPage={this.handleNextPageClick}
      />
    );
  }
}
